import React from 'react';
import {
  AppBar, Toolbar, Typography, Button, TextField, Container, Grid, Paper,
  Box, useTheme, CircularProgress, Backdrop, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
  TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CodeIcon from '@mui/icons-material/Code';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useState } from 'react';
import { FaRegCheckCircle } from "react-icons/fa";
import { IoIosHourglass } from "react-icons/io";
import { tokens } from "./theme";
import "./App.css";

const App = () => {
  const [valores, setValores] = useState([]);
  const [data, setData] = useState([]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(false);
  const [idUsuaria, setIdusuaria] = useState('');
  const [openApiModal, setOpenApiModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('cURL copiado para a área de transferência!');
  const [boletoPdfLoadingKey, setBoletoPdfLoadingKey] = useState(null);
  const [docPage, setDocPage] = useState(0);
  const [docRowsPerPage, setDocRowsPerPage] = useState(6);
  const apiHost = process.env.REACT_APP_API_URL_FIN || "http://localhost:8015";

  const curlCommand = `curl -X GET "${apiHost}/site/usuaria?numeroOns={numeroOns}" \\
  -H "Content-Type: application/json"`;

  const handleIdChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 5) setIdusuaria(value);
  };

  const handleSearch = () => {
    if (idUsuaria.length < 4) {
      alert('Por favor, preencha o Código ONS com 4 ou 5 dígitos.');
      return;
    }
    setLoading(true);
    fetch(`${apiHost}/site/usuaria?numeroOns=${idUsuaria}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        'Access-Control-Allow-Origin': '*'
      }
    })
      .then(response => response.json())
      .then(data => {
        setData(data);
        const sortedData = data
          .map(item => ({ ...item, id: item.numeroAvc }))
          .sort((b, a) => {
            const [monthA, yearA] = a.periodoContabilInt.split('-').map(Number);
            const [monthB, yearB] = b.periodoContabilInt.split('-').map(Number);
            if (yearA !== yearB) return yearA - yearB;
            return monthA - monthB;
          });
        setValores(sortedData);
        setDocPage(0);
      })
      .catch(error => {
        console.error('Erro ao buscar ID Usuária:', error);
        alert('Erro ao buscar. Verifique a conexão e tente novamente.');
      })
      .finally(() => setLoading(false));
  };

  const handleClear = () => {
    setData([]);
    setValores([]);
    setIdusuaria('');
    setDocPage(0);
  };

  /** Primeiros 6 períodos (mais recentes) — usado só no resumo financeiro */
  const ultimos6Meses = valores.slice(0, 6);
  /** Períodos exibidos na grade (paginação sobre todo o histórico) */
  const documentosPagina = valores.slice(
    docPage * docRowsPerPage,
    docPage * docRowsPerPage + docRowsPerPage
  );
  const nomeEmpresa = valores[0]?.transmissoras || '';
  const valorTotal = ultimos6Meses.reduce((acc, item) => acc + (item.totalOns || 0), 0);
  const mediaMensal = ultimos6Meses.length > 0 ? valorTotal / ultimos6Meses.length : 0;

  const LinkIcon = ({ href, hasLink, label }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 50 }}>
      {hasLink ? (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
          <FaRegCheckCircle color={colors.greenAccent[500]} size={22} />
        </a>
      ) : (
        <IoIosHourglass color="red" size={22} />
      )}
      <Typography variant="caption" sx={{ mt: 0.5 }}>{label}</Typography>
    </Box>
  );

  const downloadBoletoPagina = async (pdfUrl, pagina, loadingKey) => {
    try {
      setBoletoPdfLoadingKey(loadingKey);
      const res = await fetch(`${apiHost}/site/pdf/pagina`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: pdfUrl, pagina }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `pagina-${pagina}.pdf`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error('Erro ao baixar página do boleto:', e);
      setSnackbarMessage('Não foi possível baixar a página do boleto.');
      setSnackbarOpen(true);
    } finally {
      setBoletoPdfLoadingKey(null);
    }
  };

  const getBoletos = (item) => {
    if (Array.isArray(item.linkBoleto)) {
      return item.linkBoleto.filter(Boolean).slice(0, 3);
    }
    if (item.linkBoleto) {
      return [item.linkBoleto];
    }
    const boletos = [];
    [1, 2, 3].forEach((i) => {
      const link = item[`linkBoleto${i}`] || item[`linkBoletoParcela${i}`];
      if (link) boletos.push(link);
    });
    if (Array.isArray(item.boletos)) boletos.push(...item.boletos.filter(Boolean).slice(0, 3 - boletos.length));
    if (Array.isArray(item.linkBoletos)) boletos.push(...item.linkBoletos.filter(Boolean).slice(0, 3 - boletos.length));
    return boletos.slice(0, 3);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Cabeçalho */}
      <AppBar position="static" style={{ backgroundColor: '#008c9e' }}>
        <head>
          <title>Tropicalia Transmissora</title>
          <link rel="icon" href="../../assets/logo_trop.png" />
        </head>
        <Toolbar>
          <img
            src={`../../assets/logo_trop.png`}
            alt="Tropicalia Logo" style={{ height: 50, marginRight: 20 }} />
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            TROPICÁLIA - Transmissora de Energia S.A.
          </Typography>
          <Button color="inherit"
            href="https://tropicaliatransmissora.com.br/"
            target="_blank">Home</Button>
          <Button color="inherit" href="https://nf-tropicalia-transmissora.cust.app.br/"
            target="_blank">Notas Fiscais</Button>
          <Button color="inherit" href="http://webmail.tropicaliatransmissora.com.br/"
            target="_blank">Webmail</Button>
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ flex: 1, pb: 6, position: 'relative' }}>
      {/* Efeito de energia sendo transmitida */}
      <div className="energy-flow">
        <div className="energy-line" />
        <div className="energy-line" />
        <div className="energy-line" />
        <div className="energy-line" />
        <div className="energy-line" />
        <div className="energy-line" />
        <div className="energy-line" />
        <div className="energy-spark" />
        <div className="energy-spark" />
        <div className="energy-spark" />
        <div className="energy-spark" />
        <div className="energy-spark" />
        <div className="energy-spark" />
        <div className="energy-spark" />
      </div>
      <Container sx={{ position: 'relative', zIndex: 1 }}>
        <Paper style={{ padding: '20px', marginTop: '20px' }}>
          <Typography variant="h5" style={{ marginBottom: '20px' }}>
            Pesquisar nota/boleto
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <TextField
                label="Codigo ONS"
                fullWidth
                value={idUsuaria}
                onChange={handleIdChange}
                inputProps={{ inputMode: 'numeric', minLength: 4, maxLength: 5 }}
                placeholder="4 ou 5 dígitos"
              />
            </Grid>
            <Grid item xs={2}>
              <Button type="button" variant="contained" color="primary" onClick={handleSearch} startIcon={<SearchIcon />}>
                Pesquisa
              </Button>
            </Grid>
          </Grid>
          <Button variant="outlined" color="secondary" onClick={handleClear} style={{ marginTop: '20px' }} startIcon={<ClearIcon />}>
            Limpar
          </Button>
        </Paper>
      </Container>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {data.length > 0 && (
        <Container sx={{ mt: 3, position: 'relative', zIndex: 1 }}>
          {/* Nome da empresa */}
          <Paper sx={{ p: 2, mb: 3, background: 'linear-gradient(135deg, #008c9e 0%, #006d7a 100%)', color: 'white' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon sx={{ fontSize: 32 }} />
              <Typography variant="h5" fontWeight={600}>{nomeEmpresa}</Typography>
            </Box>
          </Paper>

          {/* Resumo: Total e Média */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ background: 'linear-gradient(135deg, #4cceac 0%, #3da58a 100%)', color: 'white', height: '100%' }}>
                <CardContent>
                  <Typography variant="overline" sx={{ opacity: 0.9 }}>Valor total emitido nos últimos 6 meses</Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ background: 'linear-gradient(135deg, #6870fa 0%, #535ac8 100%)', color: 'white', height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TrendingUpIcon />
                    <Typography variant="overline" sx={{ opacity: 0.9 }}>Média mensal</Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700}>
                    {mediaMensal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Cards por período (histórico paginado) */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Documentos por período
          </Typography>
          <Grid container spacing={2}>
            {documentosPagina.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.periodoContabil}>
                <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                  <CardContent>
                    <Typography variant="overline" color="text.secondary">{item.periodoContabil}</Typography>
                    <Typography variant="h5" color="primary" fontWeight={600} sx={{ mt: 3 }}>
                      {(item.totalOns || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                      <LinkIcon href={item.linkXml} hasLink={!!item.linkXml} label="XML" />
                      <LinkIcon href={item.linkDanfe} hasLink={!!item.linkDanfe} label="Danfe" />
                      {Array.isArray(item.linkBoleto) && getBoletos(item).length === 3 ? (
                        getBoletos(item).map((link, idx) => {
                          const pagina = idx + 1;
                          const loadingKey = `${item.periodoContabil}-${pagina}`;
                          const loading = boletoPdfLoadingKey === loadingKey;
                          return (
                            <Box
                              key={idx}
                              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56 }}
                            >
                              <Button
                                type="button"
                                variant="text"
                                color="inherit"
                                size="small"
                                disabled={loading}
                                onClick={() => downloadBoletoPagina(link, pagina, loadingKey)}
                                sx={{ minWidth: 'auto', p: 0.5 }}
                              >
                                {loading ? (
                                  <CircularProgress size={22} sx={{ color: colors.greenAccent[500] }} />
                                ) : (
                                  <FaRegCheckCircle color={colors.greenAccent[500]} size={22} />
                                )}
                              </Button>
                              <Typography variant="caption" sx={{ mt: 0.5 }}>{`Boleto ${pagina}`}</Typography>
                            </Box>
                          );
                        })
                      ) : Array.isArray(item.linkBoleto) ? (
                        getBoletos(item).map((link, idx, arr) => (
                          <LinkIcon key={idx} href={link} hasLink={!!link} label={arr.length > 1 ? `Boleto ${idx + 1}` : 'Boleto'} />
                        ))
                      ) : item.linkBoleto ? (
                        [1, 2, 3].map((page) => (
                          <LinkIcon
                            key={page}
                            href={`${item.linkBoleto}#page=${page}`}
                            hasLink={true}
                            label={`Boleto ${page}`}
                          />
                        ))
                      ) : (
                        <LinkIcon href={null} hasLink={false} label="Boleto" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {valores.length > 0 && (
            <TablePagination
              component="div"
              count={valores.length}
              page={docPage}
              onPageChange={(_, newPage) => setDocPage(newPage)}
              rowsPerPage={docRowsPerPage}
              onRowsPerPageChange={(e) => {
                setDocRowsPerPage(parseInt(e.target.value, 10));
                setDocPage(0);
              }}
              rowsPerPageOptions={[6, 12, 24, 48]}
              labelRowsPerPage="Períodos por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count !== -1 ? count : to}`
              }
              sx={{ borderTop: 1, borderColor: 'divider', mt: 2 }}
            />
          )}
        </Container>
      )}
      </Box>

      {/* Rodapé */}
      <Box
        component="footer"
        sx={{
          py: 3,
          pt: 4,
          px: 2,
          mt: 'auto',
          backgroundColor: '#008c9e',
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} sm={4}>
              <img src={`../../assets/logo_trop.png`} alt="Tropicalia Logo" style={{ height: 50 }} />
              <Typography variant="body2">
                TROPICÁLIA - Transmissora de Energia S.A.
              </Typography>
              <Button color="inherit"
                href="https://tust.app.br/"
                target="_blank">©2026 Desenvolvido por Exit Consulting</Button>
            </Grid>
            <Grid item xs={12} sm={4}>

              <Typography variant="body2">Matriz: Rua Prof. Álvaro Rodrigues, 352 - 7º andar - Botafogo - Rio de Janeiro - RJ, CEP: 22780-040</Typography>
              <Typography variant="body2">Filial: Av Governador Lomanto Junior, 1237 - Joaquim Romão, Jequié, Bahia, CEP 45.200-601</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2">Atendimento à Comunidade: 0800-777-1215</Typography>
              <Typography variant="body2">contato@tropicaliatransmissora.com.br</Typography>
              <Typography variant="body2">Tel: 21 3262-6000</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Suporte:</strong>{' '}
                <Button
                  component="a"
                  href="https://wa.me/5511918783914"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                  size="small"
                  sx={{ textTransform: 'none', minWidth: 'auto', p: 0 }}
                >
                  WhatsApp
                </Button>
                {' · '}
                <Button
                  component="a"
                  href="mailto:jose.silva@tust.app.br"
                  color="inherit"
                  size="small"
                  sx={{ textTransform: 'none', minWidth: 'auto', p: 0 }}
                >
                  Email
                </Button>
              </Typography>
              <Button
                color="inherit"
                size="small"
                startIcon={<CodeIcon />}
                onClick={() => setOpenApiModal(true)}
                sx={{ mt: 1 }}
              >
                Integração com API
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Dialog open={openApiModal} onClose={() => setOpenApiModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Chamada API - cURL (OpenAPI)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            GET /site/usuaria - Consulta usuária por código ONS (4 ou 5 dígitos)
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.100', overflow: 'auto' }}>
            <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {curlCommand}
            </Box>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Substitua {'{numeroOns}'} pelo código ONS (ex: 12345)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApiModal(false)}>Fechar</Button>
          <Button
            variant="contained"
            onClick={() => {
              const cmd = curlCommand.replace('{numeroOns}', idUsuaria || '12345');
              navigator.clipboard.writeText(cmd);
              setSnackbarMessage('cURL copiado para a área de transferência!');
              setSnackbarOpen(true);
              setOpenApiModal(false);
            }}
          >
            Copiar cURL
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default App;
